import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { createLogger } from '@/lib/logger';
const log = createLogger('AdminAuth');

interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  role: 'super_admin' | 'admin' | 'viewer';
  permissions: {
    users: boolean;
    subscriptions: boolean;
    content: boolean;
    settings: boolean;
  };
  is_active: boolean;
}

export function useAdminAuth() {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminStatus();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAdminStatus();
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async () => {
    setIsLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsAuthenticated(false);
        setAdminUser(null);
        setIsLoading(false);
        return;
      }

      const { data: adminData, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (error || !adminData) {
        setIsAuthenticated(false);
        setAdminUser(null);
      } else {
        setIsAuthenticated(true);
        setAdminUser({
          id: adminData.id,
          user_id: adminData.user_id || '',
          email: adminData.email,
          role: adminData.role as AdminUser['role'],
          permissions: adminData.permissions as AdminUser['permissions'],
          is_active: adminData.is_active ?? true,
        });
      }
    } catch (err) {
      log.error('Admin auth error:', err);
      setIsAuthenticated(false);
      setAdminUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const hasPermission = (permission: keyof AdminUser['permissions']) => {
    if (!adminUser) return false;
    if (adminUser.role === 'super_admin') return true;
    return adminUser.permissions[permission] ?? false;
  };

  return {
    adminUser,
    isLoading,
    isAuthenticated,
    isSuperAdmin: adminUser?.role === 'super_admin',
    hasPermission,
    signOut,
    refetch: checkAdminStatus,
  };
}
